import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { type PunchNoteReviewRead } from "@/lib/api";
import { fmtDateTime } from "@/lib/time";
import { displayNoteText } from "@/components/entries/EntryTable";

export const fieldNoteWrapClass =
  "text-sm whitespace-pre-wrap break-words [overflow-wrap:anywhere] [word-break:break-word] max-w-full min-w-0";

export function PunchNotesReviewList({
  rows,
  onAck,
  isProcessing,
  canAcknowledge = true,
  acknowledgeableUserIds = null,
}: {
  rows: PunchNoteReviewRead[];
  onAck?: (entryId: number) => void;
  isProcessing: boolean;
  canAcknowledge?: boolean;
  acknowledgeableUserIds?: Set<number> | null;
}) {
  if (rows.length === 0) return null;
  return (
    <div className="w-full max-w-full min-w-0 space-y-3">
      {rows.map((row) => {
        const mayAck =
          canAcknowledge &&
          !!onAck &&
          (acknowledgeableUserIds == null || acknowledgeableUserIds.has(row.user_id));
        const viewOnlyUnacked =
          canAcknowledge &&
          acknowledgeableUserIds != null &&
          !acknowledgeableUserIds.has(row.user_id);
        return (
          <div
            key={row.entry_id}
            className="w-full max-w-full min-w-0 rounded-lg border p-3 sm:p-4 border-destructive/25 bg-destructive/5"
          >
            <div className="flex flex-wrap items-start justify-between gap-2 min-w-0">
              <div className="flex flex-wrap items-center gap-2 min-w-0">
                <Badge variant="outline" className="shrink-0">
                  Punch notes
                </Badge>
                <Badge variant="destructive" className="shrink-0">
                  Unacknowledged
                </Badge>
                {viewOnlyUnacked && (
                  <Badge variant="outline" className="shrink-0 text-muted-foreground">
                    View only — not on your team
                  </Badge>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2 shrink-0">
                {row.qb_record_url ? (
                  <a
                    href={row.qb_record_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs font-medium text-primary underline-offset-4 hover:underline"
                  >
                    Open in QuickBase
                  </a>
                ) : null}
                {mayAck && (
                  <Button
                    variant="default"
                    size="sm"
                    disabled={isProcessing}
                    onClick={() => onAck!(row.entry_id)}
                  >
                    Acknowledge
                  </Button>
                )}
              </div>
            </div>

            <div className="mt-2 min-w-0">
              <div className="text-sm font-medium break-words [overflow-wrap:anywhere]">
                {row.employee_name || row.user_email || "Unknown submitter"}
              </div>
              {row.employee_name && row.user_email && (
                <div className="text-xs text-muted-foreground break-all">{row.user_email}</div>
              )}
            </div>

            <div className="mt-2 space-y-1 min-w-0">
              <div className={fieldNoteWrapClass}>
                <strong className="text-foreground/80">Meals & Breaks:</strong> {displayNoteText(row.meals_breaks)}
              </div>
              <div className={fieldNoteWrapClass}>
                <strong className="text-foreground/80">Injured:</strong> {displayNoteText(row.injured)}
              </div>
              {row.flagged_fields.map((f) => (
                <div key={f.field} className={fieldNoteWrapClass}>
                  <strong className="text-foreground/80">{f.label}:</strong> {f.text}
                </div>
              ))}
            </div>

            <div className="mt-3 text-xs text-muted-foreground min-w-0 break-words">
              <span className="font-medium text-foreground/70">Punch: </span>
              <span className="whitespace-nowrap">{fmtDateTime(row.recorded_time)}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

